import React, { useCallback, useEffect, useState } from "react";
import { Timer, Trash2, Plus, Wand2, Loader2 } from "lucide-react";
import { Modal, HlavickaOkna, OdznakOkna, Field, ListovaniMesice } from "./ui.jsx";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { apiFetch } from "../lib/api.js";
import { toast } from "./Toast.jsx";

// === Upravit fond (1.11.2026, hlášení #189) ===
// Owner u konkrétního člověka (manager, CSM) a měsíce přidá ruční úpravu fondu
// v hodinách (kladnou i zápornou) s poznámkou, typicky doplnění chybějících
// hodin z minulého měsíce. Úprava se ve fondu ukáže jako další pilulka a
// počítá s ní "má odpracovat", "k dnešku" i "zbývá". Měření ani report
// dotyčného se nemění, je to ownerovo rozhodnutí nad fondem.

const MESICE_1 = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const MESICE_2 = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];
const MESICE_6 = ["lednu", "únoru", "březnu", "dubnu", "květnu", "červnu", "červenci", "srpnu", "září", "říjnu", "listopadu", "prosinci"];
const mIdx = (mesic) => parseInt(String(mesic).slice(5, 7), 10) - 1;
const posunMesic = (mesic, n) => {
  let y = parseInt(mesic.slice(0, 4), 10), m = parseInt(mesic.slice(5, 7), 10) - 1 + n;
  y += Math.floor(m / 12); m = ((m % 12) + 12) % 12;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
};
const rozdilMesicu = (a, b) => (parseInt(a.slice(0, 4), 10) - parseInt(b.slice(0, 4), 10)) * 12 + (parseInt(a.slice(5, 7), 10) - parseInt(b.slice(5, 7), 10));
export const hodinyCz = (x) => String(Math.round((Number(x) || 0) * 10) / 10).replace(".", ",");
// Znaménko s pravým mínusem, ať se nepoplete s pomlčkou.
export const hodinySeZnamenkem = (x) => (Number(x) > 0 ? `+${hodinyCz(x)}` : `−${hodinyCz(Math.abs(Number(x)))}`);
const kdyCz = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}:\d{2}))?/.exec(String(s || ""));
  return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}${m[4] ? ` v ${m[4].replace(/^0/, "")}` : ""}` : "";
};
const ROLE_LABEL = { manager: "Manager", csm: "CSM" };

export default function FondUpravaModal({ open, person, dnesniMesic, onClose, onChanged }) {
  const [mesic, setMesic] = useState(dnesniMesic);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [hodin, setHodin] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [busy, setBusy] = useState(false);
  const [smazat, setSmazat] = useState(null);

  useEffect(() => { if (open) { setMesic(dnesniMesic); setHodin(""); setPoznamka(""); setErr(""); } }, [open, dnesniMesic]);

  const load = useCallback(async () => {
    if (!open || !person?.email) return;
    setErr(""); setData(null);
    try {
      const r = await apiFetch(`/api/time-report?fondUpravy=1&email=${encodeURIComponent(person.email)}&mesic=${mesic}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData(j);
    } catch (e) { setErr(e.message); setData(null); }
  }, [open, person?.email, mesic]);
  useEffect(() => { load(); }, [load]);

  const minuly = data?.minulyMesic || null;
  const minulyNazev = minuly ? MESICE_2[mIdx(minuly.mesic)] : "";
  // Ochrana proti dvojímu doplnění: když už v měsíci je úprava přesně na chybějící hodiny, tlačítko se vypne.
  const uzDoplneno = !!minuly && minuly.chybiHodin > 0
    && (data?.upravy || []).some((u) => Math.abs(Number(u.hodin) - minuly.chybiHodin) < 0.05);
  const predvyplnit = () => {
    if (!minuly || !(minuly.chybiHodin > 0) || uzDoplneno) return;
    setHodin(hodinyCz(minuly.chybiHodin));
    if (!poznamka.trim()) setPoznamka(`Doplnění chybějících hodin za ${MESICE_1[mIdx(minuly.mesic)].toLowerCase()}`);
  };

  const pridat = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/api/time-report", {
        method: "POST",
        body: JSON.stringify({ action: "fondUpravaPridat", email: person.email, mesic, hodin, poznamka }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      toast(`Fond za ${MESICE_1[mIdx(mesic)].toLowerCase()} upraven o ${hodinySeZnamenkem(j.hodin)} h`);
      setHodin(""); setPoznamka("");
      await load();
      onChanged?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const doSmazat = async () => {
    const u = smazat;
    if (!u) return;
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify({ action: "fondUpravaSmazat", id: u.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      setSmazat(null);
      toast("Úprava fondu smazána");
      await load();
      onChanged?.();
    } catch (e) { setErr(e.message); setSmazat(null); }
    finally { setBusy(false); }
  };

  if (!open || !person) return null;
  const fond = data?.fond || null;
  const upravy = data?.upravy || [];
  const posun = rozdilMesicu(mesic, dnesniMesic);
  const zakladFondu = fond ? (Number(fond.fondHodin) || 0) - (Number(fond.volnoHodin) || 0) : 0;

  return (
    <>
      <Modal open={open} onClose={onClose} size="md" plnaHlavicka
        header={<HlavickaOkna ikona={Timer} nazev="Upravit fond" popis={`${person.name}${ROLE_LABEL[person.role] ? `, ${ROLE_LABEL[person.role]}` : ""}`}
          odznaky={<OdznakOkna ton="zluty">Mění jen fond, ne měření</OdznakOkna>} onClose={onClose} />}
        footer={<>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Zavřít</button>
          <button type="button" className="btn btn-primary" onClick={pridat} disabled={busy || !hodin.trim() || !poznamka.trim()} aria-busy={busy ? "true" : undefined}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Přidat úpravu
          </button>
        </>}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Úprava se přičte k tomu, co má za měsíc odpracovat, a poměrnou částí i k dnešku. Měření ani report se nemění, úpravu uvidí ve své kartě Měsíční fond.
          </p>

          <div>
            <span className="label">Měsíc</span>
            <ListovaniMesice popis={`${MESICE_1[mIdx(mesic)]} ${mesic.slice(0, 4)}`}
              onZpet={() => setMesic(posunMesic(mesic, -1))} onVpred={() => setMesic(posunMesic(mesic, 1))}
              zpetVypnuto={posun <= -12} vpredVypnuto={posun >= 12}
              naDnesek={posun !== 0 ? () => setMesic(dnesniMesic) : null} />
          </div>

          {fond && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm tabular-nums">
              <span className="rounded-lg border border-slate-200 bg-white px-2 py-0.5">Bez úprav {hodinyCz(zakladFondu)} h</span>
              {upravy.length > 0 && (<>
                <span className="font-bold text-slate-400">{fond.upravaHodin < 0 ? "−" : "+"}</span>
                <span className="rounded-lg bg-plum-100 px-2 py-0.5 text-plum-900">Úpravy {hodinyCz(Math.abs(fond.upravaHodin))} h</span>
              </>)}
              <span className="font-bold text-slate-400">=</span>
              <span className="rounded-lg bg-brand-100 px-2 py-0.5 font-bold text-brand-800">Má odpracovat {hodinyCz(fond.maOdpracovatHodin)} h</span>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-sm font-bold text-slate-900">Nová úprava za {MESICE_1[mIdx(mesic)].toLowerCase()}</div>
            {minuly && minuly.uzavreny && (
              <button type="button" className="btn btn-secondary w-full" onClick={predvyplnit} disabled={!(minuly.chybiHodin > 0) || uzDoplneno}>
                <Wand2 className="h-4 w-4" />
                {uzDoplneno
                  ? `Chybějící hodiny z ${minulyNazev} už jsou doplněné`
                  : minuly.chybiHodin > 0
                  ? `Předvyplnit chybějící hodiny z ${minulyNazev} (${hodinyCz(minuly.chybiHodin)} h)`
                  : `V ${MESICE_6[mIdx(minuly.mesic)]} nic nechybělo`}
              </button>
            )}
            {minuly && minuly.uzavreny && minuly.chybiHodin > 0 && (
              <div className="flex flex-wrap gap-1.5 text-xs tabular-nums">
                <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5">V {MESICE_6[mIdx(minuly.mesic)]} mělo být {hodinyCz(minuly.maOdpracovatHodin)} h</span>
                <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5">Odpracováno {hodinyCz(minuly.odpracovanoHodin)} h</span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-900">Chybí {hodinyCz(minuly.chybiHodin)} h</span>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
              <Field label="Hodiny" hint="Kladné přidá, záporné ubere.">
                <input className="field tabular-nums" inputMode="decimal" placeholder="např. 15,8" value={hodin}
                  onChange={(e) => setHodin(e.target.value.replace(/[^0-9,.\-−]/g, "").replace("−", "-"))} />
              </Field>
              <Field label="Poznámka" hint={`${poznamka.length} z 200 znaků, uvidí ji i ve svém fondu.`}>
                <input className="field" maxLength={200} placeholder="Doplnění chybějících hodin za říjen" value={poznamka}
                  onChange={(e) => setPoznamka(e.target.value)} />
              </Field>
            </div>
          </div>

          {err && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{err}</div>}

          <div className="space-y-2">
            <div className="text-sm font-bold text-slate-900">Úpravy v {MESICE_6[mIdx(mesic)]}</div>
            {!data && !err && <div className="py-3 text-center text-sm text-slate-400">Načítám…</div>}
            {data && upravy.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500">Zatím žádná úprava, fond se počítá standardně.</div>}
            {upravy.map((u) => (
              <div key={u.id} className="polozka flex items-start gap-3">
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-sm font-bold tabular-nums ${u.hodin > 0 ? "bg-plum-100 text-plum-900" : "bg-amber-100 text-amber-900"}`}>{hodinySeZnamenkem(u.hodin)} h</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 [overflow-wrap:anywhere]">{u.poznamka}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                    {u.kdo && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">Zadal {u.kdo}</span>}
                    {u.kdy && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{kdyCz(u.kdy)}</span>}
                  </div>
                </div>
                <button type="button" className="btn-ikona" onClick={() => setSmazat(u)} disabled={busy} aria-label="Smazat úpravu" title="Smazat úpravu">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal>
      <ConfirmDialog open={!!smazat} variant="danger" loading={busy}
        title="Smazat úpravu fondu?"
        description={smazat ? `Úprava ${hodinySeZnamenkem(smazat.hodin)} h (${smazat.poznamka}) se přestane počítat do fondu.` : ""}
        confirmLabel="Smazat" onConfirm={doSmazat} onCancel={() => setSmazat(null)} />
    </>
  );
}
